import React, { useRef, useState } from 'react';
import { C, Btn, Alert } from './UI';
import { AddressAutocomplete } from './AddressAutocomplete';
import { RequestEstimateRequest } from '../services/api';
import { downscaleImage } from '../utils/downscaleImage';

const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

interface Props {
    // Pre-filled + locked for a logged-in customer; editable when submitting as a prospect.
    lockedName?: string;
    lockedEmail?: string;
    lockedPhone?: string;
    submitLabel?: string;
    onSubmit: (body: RequestEstimateRequest) => Promise<void>;
}

export function EstimateRequestForm({ lockedName, lockedEmail, lockedPhone, submitLabel = 'Request estimate', onSubmit }: Props) {
    const [name, setName] = useState(lockedName ?? '');
    const [email, setEmail] = useState(lockedEmail ?? '');
    const [phone, setPhone] = useState(lockedPhone ?? '');
    const [address, setAddress] = useState('');
    const [addressFields, setAddressFields] = useState<{ lat?: number; lng?: number; city?: string; state?: string; county?: string; postalCode?: string; country?: string }>({});
    const [notes, setNotes] = useState('');
    const [photos, setPhotos] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const addPhoto = async (file: File) => {
        if (photos.length >= MAX_PHOTOS) { setError(`You can attach up to ${MAX_PHOTOS} photos.`); return; }
        if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
        if (file.size > MAX_PHOTO_BYTES) { setError('Each photo must be under 10MB.'); return; }
        setError('');
        try {
            const { base64 } = await downscaleImage(file);
            setPhotos(prev => [...prev, base64]);
        } catch {
            setError('Could not read that photo — please try again or pick a different one.');
        }
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!address.trim()) { setError('Please select your address from the dropdown suggestions.'); return; }
        setLoading(true); setError('');
        try {
            await onSubmit({
                name: name.trim(),
                email: email.trim(),
                phone: phone.trim() || undefined,
                jobSiteAddress: address,
                jobSiteLat: addressFields.lat,
                jobSiteLng: addressFields.lng,
                jobSiteCity: addressFields.city,
                jobSiteState: addressFields.state,
                jobSiteCounty: addressFields.county,
                jobSitePostalCode: addressFields.postalCode,
                jobSiteCountry: addressFields.country,
                notes: notes.trim() || undefined,
                photosBase64: photos,
            });
            setDone(true);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    if (done) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Request sent</div>
                <div style={{ fontSize: 13, color: C.textSub }}>We'll review your photos and details and be in touch shortly with your estimate.</div>
            </div>
        );
    }

    return (
        <form onSubmit={submit}>
            <Alert message={error} />
            {!lockedName && (
                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Full name</label>
                    <input value={name} onChange={e => setName(e.target.value)} required
                        style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
            )}
            {!lockedEmail && (
                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                        style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
            )}
            {!lockedPhone && (
                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Phone (optional)</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
            )}

            <AddressAutocomplete
                label="Job site address"
                value={address}
                required
                onChange={r => { setAddress(r.address); setAddressFields({ lat: r.lat, lng: r.lng, city: r.city, state: r.state, county: r.county, postalCode: r.postalCode, country: r.country }); }}
            />

            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>
                    Tell us about the job <span style={{ color: C.textMuted, fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
                    placeholder="What needs to be done? Anything we should know before we quote it?"
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>
                    Photos <span style={{ color: C.textMuted, fontWeight: 400 }}>(optional, up to {MAX_PHOTOS} — helps us quote accurately)</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8 }}>
                    {photos.map((p, i) => (
                        <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, aspectRatio: '1' }}>
                            <img src={`data:image/jpeg;base64,${p}`} alt={`Job photo ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            <button type="button" onClick={() => setPhotos(prev => prev.filter((_, x) => x !== i))}
                                style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', width: 22, height: 22, color: '#fff', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                        </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                        <div onClick={() => fileRef.current?.click()} style={{ aspectRatio: '1', border: `2px dashed ${C.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: C.bg }}>
                            <div style={{ fontSize: 22 }}>📷</div>
                            <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>Add photo</div>
                            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                                onChange={e => { const f = e.target.files?.[0]; if (f) addPhoto(f); e.target.value = ''; }} />
                        </div>
                    )}
                </div>
            </div>

            <Btn type="submit" fullWidth disabled={loading}>{loading ? 'Sending…' : submitLabel}</Btn>
        </form>
    );
}
